import { useState } from "react";
import { DndContext, DragOverlay, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { motion } from "framer-motion";
import { Check, X, ArrowRightLeft, RotateCcw } from "lucide-react";
import { fmtUSD, fmtDate } from "@/lib/api";
import { PersonAvatar } from "@/components/PersonAvatar";
import { PolicyChip } from "@/components/StatusChip";
import { ViolationTooltip } from "@/components/expenses/ViolationTooltip";

const COLUMNS = [
  { key: "needs_review", label: "Needs review", dot: "#4F8EF7" },
  { key: "waiting", label: "Waiting", dot: "#F59E0B" },
  { key: "approved", label: "Approved", dot: "#22C55E" },
  { key: "rejected", label: "Rejected", dot: "#EF4444" },
];

const KanbanCard = ({ exp, dragging, column, onQuickAction }) => (
  <div
    className={`card-lift rounded-xl border bg-[#161616] p-3 ${
      dragging ? "border-[rgba(79,142,247,0.5)] shadow-[0_18px_44px_rgba(0,0,0,0.6)] ring-1 ring-[rgba(79,142,247,0.4)]" : "border-[#222] hover-lift"
    }`}
  >
    <div className="flex items-center justify-between gap-2">
      <span className="truncate text-[13px] font-medium text-white">{exp.merchant}</span>
      <span className="shrink-0 text-[13px] font-semibold tabular-nums text-white">{fmtUSD(exp.amount)}</span>
    </div>
    <div className="mt-1.5 flex items-center gap-1.5">
      <PersonAvatar name={exp.employee_name} size={18} />
      <span className="truncate text-[11px] text-[#A3A3A3]">{exp.employee_name}</span>
      <span className="ml-auto text-[10.5px] text-[#555]">{fmtDate(exp.date)}</span>
    </div>
    {exp.policy_status !== "ok" && (
      <div className="mt-2">
        <ViolationTooltip exp={exp}>
          <PolicyChip policyStatus={exp.policy_status} />
        </ViolationTooltip>
      </div>
    )}
    {column === "needs_review" && onQuickAction && (
      <div className="mt-2.5 flex gap-1.5" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
        <button onClick={() => onQuickAction(exp.id, "approve")} data-testid={`kanban-approve-${exp.id}`}
          className="flex h-6 flex-1 items-center justify-center gap-1 rounded-md bg-[rgba(34,197,94,0.14)] text-[11px] font-medium text-[#4ADE80] transition-colors hover:bg-[rgba(34,197,94,0.28)]">
          <Check size={11} /> Approve
        </button>
        <button onClick={() => onQuickAction(exp.id, "reject")} data-testid={`kanban-reject-${exp.id}`}
          className="flex h-6 flex-1 items-center justify-center gap-1 rounded-md bg-[rgba(239,68,68,0.1)] text-[11px] font-medium text-[#F87171] transition-colors hover:bg-[rgba(239,68,68,0.24)]">
          <X size={11} /> Reject
        </button>
      </div>
    )}
    {column === "rejected" && onQuickAction && (
      <div className="mt-2.5 flex gap-1.5" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
        <button onClick={() => onQuickAction(exp.id, "restore")} data-testid={`kanban-restore-${exp.id}`}
          className="flex h-6 flex-1 items-center justify-center gap-1 rounded-md bg-[rgba(163,163,163,0.12)] text-[11px] font-medium text-[#A3A3A3] transition-colors hover:bg-[rgba(163,163,163,0.24)] hover:text-white">
          <RotateCcw size={11} /> Restore to review
        </button>
      </div>
    )}
  </div>
);

const DraggableCard = ({ exp, column, onOpen, onQuickAction }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: exp.id,
    data: { column, exp },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => onOpen?.(exp.id)}
      data-testid={`kanban-card-${exp.id}`}
      data-aria-key={`expense:${exp.id}`}
      data-aria-label={`${exp.merchant} ${fmtUSD(exp.amount)} — ${exp.employee_name}`}
      className={`cursor-grab active:cursor-grabbing ${isDragging ? "opacity-30" : ""}`}
    >
      <KanbanCard exp={exp} column={column} onQuickAction={onQuickAction} />
    </div>
  );
};

const Column = ({ col, items, onOpen, onQuickAction, headerAction }) => {
  const { setNodeRef, isOver } = useDroppable({ id: col.key });
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-column-${col.key}`}
      data-aria-key={`queue:${col.key}`}
      data-aria-label={`${col.label} queue (${items.length} expenses)`}
      className={`flex min-h-[420px] w-full flex-col rounded-[14px] border bg-[#111111] transition-colors ${
        isOver ? "border-[rgba(79,142,247,0.45)]" : "border-[#222]"
      }`}
    >
      <div className="flex items-center gap-2 border-b border-[#1c1c1c] px-3.5 py-2.5">
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: col.dot }} />
        <span className="whitespace-nowrap text-[12px] font-medium text-white">{col.label}</span>
        <span className="ml-auto rounded-md bg-[#1c1c1c] px-1.5 text-[10px] tabular-nums text-[#6b6b6b]">{items.length}</span>
        {headerAction}
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-2.5" style={{ maxHeight: 560 }}>
        {items.map((exp) => (
          <DraggableCard key={exp.id} exp={exp} column={col.key} onOpen={onOpen} onQuickAction={onQuickAction} />
        ))}
        {items.length === 0 && (
          <div className="flex h-24 items-center justify-center rounded-xl border border-dashed border-[#242424] text-[11px] text-[#555]">
            Nothing here
          </div>
        )}
      </div>
    </div>
  );
};

export const ApprovalsKanban = ({ board, onMove, onOpen, onQuickAction, onDataMovement }) => {
  const [active, setActive] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(e) => setActive(e.active.data.current?.exp)}
      onDragEnd={(e) => {
        const from = e.active.data.current?.column;
        const to = e.over?.id;
        setActive(null);
        if (to && from && to !== from) onMove(e.active.id, from, to);
      }}
      onDragCancel={() => setActive(null)}
    >
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
        className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {COLUMNS.map((col) => (
          <Column key={col.key} col={col} items={board[col.key] || []} onOpen={onOpen} onQuickAction={onQuickAction}
            headerAction={col.key === "approved" && onDataMovement ? (
              <button onClick={onDataMovement} data-testid="kanban-data-movement-button" title="Export and Archive rules"
                className="flex h-6 items-center gap-1 whitespace-nowrap rounded-md bg-[rgba(79,142,247,0.12)] px-2 text-[10.5px] font-medium text-[#7FAEF9] transition-colors hover:bg-[rgba(79,142,247,0.24)]">
                <ArrowRightLeft size={10} /> Data Movement
              </button>
            ) : null} />
        ))}
      </motion.div>
      <DragOverlay>{active ? <div className="w-64"><KanbanCard exp={active} dragging /></div> : null}</DragOverlay>
    </DndContext>
  );
};
